import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Home, Bed, Bath, Wifi, Wind, Utensils, Car, ChevronLeft, Check, Trees, Umbrella, ShowerHead, Warehouse, Hammer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const amenityIcons = {
  'WiFi': Wifi,
  'Aria Condizionata': Wind,
  'Cucina Attrezzata': Utensils,
  'Parcheggio': Car,
  'Parcheggio Coperto': Warehouse,
  'Giardino Privato': Trees,
  'Pergola con Zanzariera': Umbrella,
  'Pergola Laterale': Umbrella,
  'Pergola Esterna': Umbrella,
  'Doccia Esterna': ShowerHead,
  'Veranda': Home,
  'Lavanderia': Home,
  'Nuova Costruzione': Home
};

const PropertyDetailPage = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Errore nel caricamento dell\'appartamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <p className="text-[#666666]">Caricamento...</p>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <p className="text-[#666666] mb-4">Appartamento non trovato</p>
          <Link to="/appartamenti" className="text-[#2C5263] hover:underline">
            Torna agli appartamenti
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="property-detail-page" className="min-h-screen pt-20 bg-white">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/appartamenti"
          data-testid="back-to-properties"
          className="inline-flex items-center gap-2 text-[#2C5263] hover:text-[#1A3644] transition-colors"
        >
          <ChevronLeft size={20} />
          <span>Torna agli Appartamenti</span>
        </Link>
      </div>

      {/* WIP Banner */}
      {property.status === 'wip' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div
            data-testid="wip-banner"
            className="bg-gradient-to-r from-[#1A3644] to-[#2C5263] text-white rounded-lg p-6 md:p-8 flex items-start gap-4 border-l-4 border-[#D4AF37]"
          >
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
              <Hammer size={22} className="text-[#D4AF37]" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 mb-2">
                <span className="text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-semibold">{t('properties.wipBadge')}</span>
                <span className="w-1 h-1 rounded-full bg-[#D4AF37]/50" />
                <span className="text-[#D4AF37] text-[10px] tracking-[0.2em] uppercase font-semibold">{t('properties.wipReadyFrom')}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-serif mb-2">{t('properties.wipDetailTitle')}</h3>
              <p className="text-sm text-white/85 leading-relaxed max-w-3xl">{t('properties.wipDetailText')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Image */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <div className="aspect-[21/9] bg-[#F9F6F0] rounded-lg overflow-hidden">
          <img
            src={property.images?.[0] || "https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGVsZWdhbnR8ZW58MHx8fHwxNzg0NjI1NDg0fDA&ixlib=rb-4.1.0&q=85"}
            alt={property.name}
            className={`w-full h-full object-cover ${property.status === 'wip' ? 'grayscale-[20%]' : ''}`}
          />
        </div>
        {property.images && property.images.length > 1 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {property.images.slice(1).map((img, idx) => (
              <div key={img} className="aspect-square rounded-lg overflow-hidden bg-[#F9F6F0]">
                <img
                  src={img}
                  alt={`${property.name} ${idx + 2}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Property Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <h1
              data-testid="property-name"
              className="text-4xl sm:text-5xl font-serif text-[#1A1A1A] mb-4"
            >
              {property.name}
            </h1>

            <div className="flex flex-wrap gap-6 mb-8 text-[#666666]">
              <div className="flex items-center gap-2">
                <Users size={20} />
                <span>{property.max_guests} ospiti</span>
              </div>
              <div className="flex items-center gap-2">
                <Bed size={20} />
                <span>{property.bedrooms} camere</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath size={20} />
                <span>{property.bathrooms} bagni</span>
              </div>
              <div className="flex items-center gap-2">
                <Home size={20} />
                <span>{property.size_sqm} m²</span>
              </div>
            </div>

            <div className="border-t border-[#2C5263]/10 pt-8 mb-8">
              <h2 className="text-2xl font-serif text-[#1A1A1A] mb-4">Descrizione</h2>
              <p className="text-[#666666] leading-relaxed">{property.description}</p>
            </div>

            <div className="border-t border-[#2C5263]/10 pt-8">
              <h2 className="text-2xl font-serif text-[#1A1A1A] mb-6">Servizi Inclusi</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {property.amenities.map((amenity) => {
                  const Icon = amenityIcons[amenity] || Check;
                  return (
                    <div
                      key={amenity}
                      data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, '-')}`}
                      className="flex items-center gap-3 p-4 bg-[#F9F6F0] rounded-lg"
                    >
                      <Icon size={20} className="text-[#2C5263] flex-shrink-0" />
                      <span className="text-[#1A1A1A]">{amenity}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-[#F9F6F0] p-8 rounded-lg border border-[#2C5263]/10">
              {property.status === 'wip' ? (
                <div data-testid="wip-booking-card">
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mb-4">
                    <Hammer size={24} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-2xl font-serif text-[#1A1A1A] mb-3">{t('properties.wipBookingTitle')}</h3>
                  <p className="text-sm text-[#666666] leading-relaxed mb-6">{t('properties.wipBookingText')}</p>

                  <div className="bg-white p-4 rounded-lg border border-[#D4AF37]/30 mb-6 text-center">
                    <p className="text-[10px] text-[#D4AF37] tracking-[0.2em] uppercase font-semibold mb-1">{t('properties.wipBadge')}</p>
                    <p className="text-lg font-serif text-[#1A1A1A]">{t('properties.wipReadyFrom')}</p>
                  </div>

                  <Link
                    to={`/contatti`}
                    data-testid="wip-notify-button"
                    className="block w-full bg-[#2C5263] text-white text-center px-6 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors"
                  >
                    {t('properties.wipNotifyButton')}
                  </Link>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-serif text-[#1A1A1A] mb-6">Prezzi</h3>

                  <div className="space-y-4 mb-8">
                    <div className="flex justify-between items-center">
                      <span className="text-[#666666]">Bassa stagione</span>
                      <span className="text-lg font-semibold text-[#1A1A1A]">
                        €{property.price_low_season}/notte
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#666666]">Media stagione</span>
                      <span className="text-lg font-semibold text-[#1A1A1A]">
                        €{property.price_mid_season}/notte
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#666666]">Alta stagione</span>
                      <span className="text-lg font-semibold text-[#1A1A1A]">
                        €{property.price_high_season}/notte
                      </span>
                    </div>
                  </div>

                  <Link
                    to={`/prenota?property=${property.id}`}
                    data-testid="book-now-button"
                    className="block w-full bg-[#2C5263] text-white text-center px-6 py-4 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors"
                  >
                    Richiedi Disponibilità
                  </Link>

                  <p className="text-xs text-[#666666] text-center mt-4">
                    Riceverai conferma entro 24 ore
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetailPage;
